<script setup lang="ts">
import { computed, resolveComponent, useSlots } from 'vue';
import type { SocialLink as SocialLinkType } from '@pkg/sanity-utils/linkQueries';
import { type LinkType, getLinkKey } from '@pkg/link';
import { AppStore } from '@pkg/sanity-utils/constants/appstore';
import { colors } from '@pkg/config/tailwind/colors';
import { useAppStoresConfig } from '@pkg/modular-blocks/frontend/appStores';
import AppStoreBadge from '@pkg/modular-blocks/blocks/inner/AppStore/AppStore.vue';
import CmsIcon from '@pkg/modular-blocks/blocks/inner/Tiles/CmsIcon.vue';
import CustomLink from '@pkg/link/src/components/CustomLink.vue';
import BaseFooter from './BaseFooter.vue';
import TGTGFooterText from './TGTGFooterText.vue';

const props = withDefaults(
  defineProps<{
    pageLinks?: LinkType[];
    socialLinks?: SocialLinkType[];
    legalLinks?: LinkType[];
    footnote?: string;
    /**
     * `simple` — business chrome: logo, page links, social, Apple and Google downloads, wordmark, copyright.
     * `full` — www: slots for logo, social, app stores, wordmark.
     */
    variant?: 'simple' | 'full';
    /**
     * Fixed back panel revealed as the page scrolls off it. The host layout must render
     * the footer outside its `.c-layout` (z-index: page) for the page to slide over it.
     */
    parallax?: boolean;
  }>(),
  {
    socialLinks: () => [],
    pageLinks: () => [],
    legalLinks: () => [],
    footnote: () => '',
    variant: 'simple',
    parallax: true,
  },
);

const slots = useSlots();
const appStores = useAppStoresConfig();
const hasStoreBadges = computed(() => {
  const stores = appStores.value;
  if (!stores) {
    return false;
  }

  return Boolean(
    stores[AppStore.APPLE_APP_STORE_BADGE] || stores[AppStore.GOOGLE_PLAY_STORE_BADGE],
  );
});
const showAppStores = computed(() => Boolean(slots['app-stores']) || hasStoreBadges.value);
const socialIconStyle = { fill: colors().green['30'], color: colors().green['30'] };

const TGTGLogo = resolveComponent('TGTGLogo');
const hasTgtgLogo = typeof TGTGLogo !== 'string';
</script>

<template>
  <BaseFooter :parallax="props.parallax">
    <template #main>
      <div
        v-if="footnote"
        class="footnote flex flex-col py-32 gap-32 tablet-wide:flex-row justify-between text-white items-center"
      >
        <span class="text-center m-auto">
          {{ footnote }}
        </span>
      </div>

      <div
        class="flex flex-col py-32 gap-32 tablet-wide:flex-row justify-between text-white items-center"
      >
        <div class="logo-container hidden tablet-wide:flex">
          <slot name="logo">
            <component
              :is="TGTGLogo"
              v-if="hasTgtgLogo"
              theme="light"
              header-logo
            />
          </slot>
        </div>

        <div class="flex flex-col w-fit items-center gap-32 tablet-wide:gap-16">
          <div
            v-if="pageLinks.length > 0"
            class="flex flex-row flex-wrap max-w-full items-center justify-center gap-32 text-h6 uppercase"
          >
            <CustomLink
              v-for="link in pageLinks"
              :key="getLinkKey(link)"
              :link="link"
            />
          </div>

          <div
            v-if="socialLinks.length > 0"
            class="flex flex-row flex-wrap items-center justify-center gap-x-24 text-32 text-white"
          >
            <slot name="social">
              <NuxtLink
                v-for="link in socialLinks"
                :key="link._key || link.url"
                :to="link.url"
                target="_blank"
                rel="noopener"
                :aria-label="`Visit Too Good To Go's ${link.title}`"
              >
                <CmsIcon :name="link.icon" :style="socialIconStyle" />
              </NuxtLink>
            </slot>
          </div>

          <div v-if="showAppStores" class="app-store-section flex flex-row gap-x-8">
            <slot name="app-stores">
              <AppStoreBadge :app-store="AppStore.APPLE_APP_STORE" />
              <AppStoreBadge :app-store="AppStore.GOOGLE_PLAY_STORE" />
            </slot>
          </div>
        </div>

        <!-- Balances the logo column so the links stay optically centred. -->
        <div class="logo-container hidden tablet-wide:flex" aria-hidden="true" />
      </div>
    </template>

    <template #back>
      <div class="w-full max-w-[1600px] mx-auto desk:py-32">
        <slot name="wordmark">
          <TGTGFooterText />
        </slot>
      </div>
      <div class="flex flex-col py-32 gap-24 desk:gap-12 items-center text-center">
        <div v-if="legalLinks.length > 0" class="wrapper c-footer__page-links">
          <CustomLink
            v-for="link in legalLinks"
            :key="getLinkKey(link)"
            :link="link"
          />
        </div>
        <p>Copyright © Too Good To Go ApS. All Rights Reserved.</p>
      </div>
    </template>
  </BaseFooter>
</template>

<style scoped>
.logo-container {
  height: 80px;
  flex-direction: row;
  align-items: center;

  @screen tablet-wide {
    width: 80px;
  }
}

.c-footer__page-links {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  padding: 0;

  @screen desk {
    flex-direction: row;
  }
}

:lang(en-au) .footnote {
  span {
    display: flex;
    gap: 8px;
    font-size: 12px;
    flex-direction: column-reverse;
    align-items: center;
    max-width: 850px;

    &::before,
    &::after {
      content: '';
      display: block;
      width: 30px;
      height: 18px;
      background-size: cover;
      background-position: center;
    }

    &::before {
      order: 1;
      background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 6"><rect width="10" height="6" fill="%23c00"/><rect width="10" height="3"/><circle cx="5" cy="3" r="1.5" fill="%23ff0"/></svg>');
    }

    &::after {
      filter: brightness(1.25);
      background-image: url('../assets/flags/torres_strait_islanders.png');
    }

    @screen tablet-wide {
      align-items: baseline;
      flex-direction: row-reverse;
      gap: 0 16px;

      &::before,
      &::after {
        position: relative;
        top: 4px;
      }

      &::after {
        margin-right: -6px;
      }
    }
  }
}
</style>
